import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    Divider,
    Grid,
    Paper,
    Stack,
    Tooltip,
    ToggleButton,
    Box,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';

// icons
import { Envelope } from '@phosphor-icons/react';

// components
import { PdfViewer } from 'src/modules/components';
import { useAuthenticator } from '@aws-amplify/ui-react';
import CommunicationMessage from './CommunicationMessage';

interface CommunicationDialogProps {
    open: boolean;
    onClose: () => void;
    alert_id?: string;
    thread_id?: string;
    alert_name?: string;
    client_id?: string;
    view?: string;
}

const CommunicationDialog: React.FC<CommunicationDialogProps> = props => {
    const { open, onClose, alert_id, thread_id, alert_name, client_id, view } =
        props;
    const { user } = useAuthenticator(context => [context.user]);
    const [newEmail, setNewEmail] = useState(false);
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('xl'));

    const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);

    // Callback function to receive invoice URL from child
    const handleInvoiceUrlReceived = (url: string) => {
        setInvoiceUrl(url);
    };

    // The core content we want to render, whether or not we're in a Dialog
    const content = (
        <Grid container spacing={2}>
            <Grid item xs={8} xl={7}>
                {alert_name && (
                    <Typography id="dialogTitle" variant="h4" gutterBottom>
                        {alert_name}
                    </Typography>
                )}
                <PdfViewer invoiceUrl={invoiceUrl || ''} />
            </Grid>
            <Grid item xs={4} xl={5}>
                <Box>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {/* Left: Title */}
                        <Typography id="dialogTitle" variant="h4">
                            Communication Thread
                        </Typography>

                        {/* Right: Toggle Button */}
                        <Tooltip title="New Thread">
                            <ToggleButton
                                value="check"
                                color="primary"
                                size="small"
                                selected={newEmail}
                                onChange={() =>
                                    setNewEmail(prevSelected => !prevSelected)
                                }
                            >
                                <Envelope
                                    size={24}
                                    color="#1D4A74"
                                    weight="fill"
                                />
                            </ToggleButton>
                        </Tooltip>
                    </Stack>
                </Box>

                <Divider sx={{ my: 1 }} />
                <CommunicationMessage
                    userEmail={user.attributes?.email || ''}
                    alert_id={alert_id}
                    thread_id={thread_id}
                    view={view}
                    client_id={client_id}
                    new_email={newEmail}
                    basePath={
                        view === 'email-alert'
                            ? '/email_threads/'
                            : '/alerts_messages_uploads/'
                    }
                    onInvoiceUrlReceived={handleInvoiceUrlReceived}
                />
            </Grid>
        </Grid>
    );

    if (view === 'email-alert' && !isXs) {
        return <Paper sx={{ p: 2 }}>{content}</Paper>;
    }

    return (
        <Dialog open={open} maxWidth="xl" fullWidth onClose={onClose}>
            <DialogContent>{content}</DialogContent>
        </Dialog>
    );
};

export default CommunicationDialog;
